'use client';
import React, { useMemo, useRef } from 'react';
import { exams } from '../../../../api/exams';
import { useMutation, useQuery } from 'react-query';
import toast, { LoaderIcon } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import ProgressBar from './Progress';
import Rating from '@mui/material/Rating';
import Box from '@mui/material/Box';
import StarIcon from '@mui/icons-material/Star';
import JoditEditor from 'jodit-react';
import Modal from './Modal';

type Question = {
  id: string;
  text: string;
  section: { title: string };
  rating_range_from: number;
  rating_range_to: number;
  type: string;
  tag?: string;
  answers: { id: string; text: string; rating: number }[];
};

type Props = {
  questions: Question[];
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
};

const Employee = ({ questions, currentIndex, setCurrentIndex }: Props) => {
  const question = questions[currentIndex];
  const navigate = useNavigate();
  const editor = useRef(null);
  const [hover, setHover] = React.useState(-1);
  const [isResponseModalOpen, setIsResponseModalOpen] = React.useState(false);
  // Culture audit only: disambiguates which of a UserExam's assessments is
  // being saved/submitted (see SurveyCard.tsx's takeSurvey). "" for
  // 360/appraisal, same as before this was added.
  const activeAssessmentId = window.sessionStorage.getItem('active-assessment-assessment-id') || undefined;
  const activeAssessment = JSON.parse(
    window.sessionStorage.getItem('active-assessment') || '{}'
  );

  const canviewResponse = activeAssessment?.can_access_participant_responses

  // Culture audit clients don't want section titles shown to participants.
  const isCultureAudit = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('user') || '{}')?.sub_type === 'culture';
    } catch (e) {
      return false;
    }
  })();

  // Culture audit on laptop/desktop: the client wants the survey to fill most
  // of the screen, so widen the question card and scale up text/controls at
  // lg+. Empty string elsewhere so 360/appraisal and mobile are unchanged.
  const large = (classes: string) => (isCultureAudit ? classes : '');

  // Client branding (set at login) — same theme_colour override the
  // dashboard/self-review pages use; falls back to the Tailwind primary.
  const themeColour: string | undefined = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('branding') || '{}')?.data?.appearance?.theme_colour || undefined;
    } catch (e) {
      return undefined;
    }
  }, []);

  const {
    data: participantResponses,
    isLoading: participantResponsesLoading,
    isError: participantResponsesError
  } = useQuery(
    ["participant-responses", canviewResponse],
    () => canviewResponse ? exams.getParticipantResponse({
      userId: activeAssessment?.user_id,
      userExamId: activeAssessment?.user_exam_id
    }) : Promise.resolve({}),
    {
      enabled: canviewResponse,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      select: (data: any) => data?.data,
    }
  )
  console.log("participantResponses", participantResponses)

  const currentParticipantResponse = useMemo(() => {
    if (!Array.isArray(participantResponses)) return null;
    return participantResponses.find((res: any) => res.question_id === question.id);
  }, [participantResponses, question.id]);

  const userId =
    JSON.parse(sessionStorage.getItem('user') || '{}')?.user?.user_id ||
    JSON.parse(sessionStorage.getItem('user') || '{}')?.user_id ||
    window.sessionStorage.getItem('user_id');

  const assessmentType = sessionStorage.getItem('assessment-type');

  // Answers the participant has changed are also kept on this device until a
  // save or submit succeeds, so a failed save (e.g. an expired session)
  // doesn't lose them: reopening the survey restores them.
  const localAnswersKey = `survey-answers:${assessmentType}:${window.sessionStorage.getItem('active-assessment-id') || ''}:${activeAssessmentId || ''}:${activeAssessment?.user_id || ''}`;
  const restoredAnswers = useMemo(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(localAnswersKey) || '{}');
      const restored: { [questionId: string]: any } = {};
      for (const q of questions) {
        // duration reset: some of it may already have been saved, and the
        // server adds what it's sent rather than replacing it.
        if (stored[q.id]?.edited) restored[q.id] = { ...stored[q.id], duration: 0, startTime: Date.now() };
      }
      return restored;
    } catch (e) {
      return {};
    }
  }, []);

  const [answers, setAnswers] = React.useState<{
    [questionId: string]: {
      rating: number | null;
      explanation: string;
      duration: number;
      startTime: number;
      selectedAnswers: { answerId: string; text: string }[];
      selectedAnswerIds: string[]; // For satisfaction scale and select types
      edited?: boolean; // changed by the participant in this browser
    };
  }>(restoredAnswers);

  React.useEffect(() => {
    const edited = Object.keys(answers).reduce((acc: any, id) => {
      if ((answers as any)[id]?.edited) acc[id] = (answers as any)[id];
      return acc;
    }, {});
    if (!Object.keys(edited).length) return;
    try {
      localStorage.setItem(localAnswersKey, JSON.stringify(edited));
    } catch (e) {
      // storage blocked or full — saving to the server still works
    }
  }, [answers]);

  const clearLocalAnswers = () => {
    try {
      localStorage.removeItem(localAnswersKey);
    } catch (e) {
      // ignore
    }
  };

  const currentAnswer = answers[question.id] || {
    rating: null,
    explanation: '',
    selectedAnswerIds: []
  };

  const [error, setError] = React.useState<string>('');

  // An expired session can't be fixed by retrying: the API client drops the
  // token on the first 401, after which every request is rejected with a 400
  // "no authorization token" (and a null user id). Catch it before sending,
  // and on either response, and ask the participant to sign in again.
  const [sessionExpired, setSessionExpired] = React.useState(false);

  const hasValidSession = () => {
    const token = sessionStorage.getItem('userToken');
    if (!token) return false;
    try {
      const { exp } = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return !exp || exp * 1000 > Date.now();
    } catch (e) {
      return true; // not a token we can read — let the server decide
    }
  };

  const isSessionError = (err: any) => {
    const status = err?.response?.status;
    const message = String(err?.response?.data?.message || '').toLowerCase();
    return status === 401 || (status === 400 && message.includes('authorization token'));
  };

  // Log the failure so it's visible in the console, then show the right message.
  const handleRequestError = (err: any, fallback: string) => {
    console.error(fallback, err?.response?.status, err?.response?.data || err);
    toast.remove();
    if (isSessionError(err)) {
      setSessionExpired(true);
    } else {
      toast.error(fallback);
    }
  };

  // Culture audit stores its access code at login, so the participant can
  // go straight back in; other surveys sign in from the home page.
  const signInAgain = () => {
    const accessCode = sessionStorage.getItem('survey_access_code');
    if (accessCode) {
      window.location.assign(`/survey/login/${encodeURIComponent(accessCode)}`);
    } else {
      navigate('/');
    }
  };

  const finishSurveyMutation = useMutation(
    (assessmentType === 'review'
      ? exams?.gradeSurveyReviewv2
      : exams.gradeAssessmentv2) as any,
    {
      onSuccess: () => {
        clearLocalAnswers();
        toast.remove();
        toast.success(
          `${assessmentType === 'review' ? 'Review' : 'Survey'} submitted successfully`
        );
        navigate(`/candidate/${assessmentType === 'review' ? 'my-review' : 'self-review'}`);
      },
      onError: (err: any) => {
        handleRequestError(err, `Error Submitting ${assessmentType === 'review' ? 'Review' : 'Survey'}`);
      },
    }
  );

  const updateAnswersMutation = useMutation(
    (assessmentType === 'review' ? exams.updateAnswersv2Review : exams.updateAnswersv2) as any,
    {
      onSuccess: () => {
        finishSurveyMutation.mutate({
          userId: assessmentType === 'review' ? activeAssessment?.user_id : userId,
          reviewerId: assessmentType === 'review' ? userId : null,
          examId: window.sessionStorage.getItem('active-assessment-id'),
          userExamId: window.sessionStorage.getItem('active-assessment-user-id'),
          assessmentId: activeAssessmentId,
          attempt: 1,
        } as any);
      },
      onMutate: () => {
        toast.loading(`Submitting ${assessmentType === 'review' ? 'Review' : 'Survey'}`);
      },
      onError: (err: any) => {
        handleRequestError(err, `Error Submitting ${assessmentType === 'review' ? 'Review' : 'Survey'}`);
      },
    }
  );

  const draftAnswersMutation = useMutation(
    (assessmentType === 'review' ? exams.updateAnswersv2Review : exams.updateAnswersv2) as any,
    {
      onSuccess: () => {
        clearLocalAnswers();
        toast.remove();
        navigate(`/candidate/${assessmentType === 'review' ? 'my-review' : 'dashboard'}`);
      },
      onMutate: () => {
        toast.loading(`Saving ${assessmentType === 'review' ? 'Review' : 'Survey'}`);
      },
      onError: (err: any) => {
        handleRequestError(err, `Error Saving ${assessmentType === 'review' ? 'Review' : 'Survey'}`);
      },
    }
  );

  // A question's answer state as loaded from its saved draft (answers with
  // status === true).
  const buildInitialAnswer = (q: Question) => {
    const firstAnswer: any = q?.answers?.[0];
    const type = q?.type?.toLowerCase() || '';

    // Get previously selected answers (where status === true)
    const savedAnswers: any[] = q?.answers?.filter((ans: any) => ans?.status === true) || [];

    let initialAnswer: any = {
      rating: firstAnswer?.rating ?? null,
      explanation: '',
      selectedAnswerIds: [],
      selectedAnswers: [],
      duration: 0,
      startTime: Date.now(),
    };

    // For satisfaction scale - load saved selection
    if (type.includes('satisfaction scale') && savedAnswers.length > 0) {
      initialAnswer.selectedAnswerIds = [savedAnswers[0].id];
      initialAnswer.explanation = savedAnswers[0]?.selected_answer_text || savedAnswers[0].text;
    }

    // For select type - load all saved selections
    if (type.includes('select') && savedAnswers.length > 0) {
      initialAnswer.selectedAnswers = savedAnswers.map((ans: any) => ({
        answerId: ans.id,
        text: ans?.selected_answer_text || ans?.text
      }));
    }

    // For free text / fill in the blank - load saved text
    if ((type.includes('free text') || type.includes('fill in the blank')) && firstAnswer?.selected_answer_text) {
      initialAnswer.explanation = firstAnswer?.selected_answer_text;
    }

    return initialAnswer;
  };

  // Answer state for any question, including ones not revisited since a
  // draft was resumed — resuming jumps to the last answered question, so
  // earlier questions never pass through the effect below. Without this,
  // Submit/Save Draft sent them with empty answers and the backend wiped
  // their saved draft answers.
  const getAnswerState = (q: Question) => answers[q.id] || buildInitialAnswer(q);

  // Resume where the participant left off: the last question saved on the
  // server, or further on if answers kept on this device go further.
  React.useEffect(() => {
    const serverAnswered = questions.filter(q =>
      q.answers?.some((ans: any) => ans?.status === true)
    ).length;
    let resumeAt = serverAnswered - 1;
    questions.forEach((q, i) => {
      if (restoredAnswers[q.id]) resumeAt = Math.max(resumeAt, i);
    });
    if (resumeAt >= 0) setCurrentIndex(resumeAt);
  }, []);

  React.useEffect(() => {
    const existingAnswer = answers[question.id];
    // If answer doesn't exist yet, initialize it with saved data
    if (!existingAnswer) {
      setAnswers((prev) => ({
        ...prev,
        [question.id]: buildInitialAnswer(question),
      }));
    }
  }, [question.id]);

  // Track time per question
  React.useEffect(() => {
    const existingAnswer = answers[question.id];
    const firstAnswer = question?.answers?.[0];

    // setAnswers((prev) => ({
    //   ...prev,
    //   [question.id]: {
    //     rating: existingAnswer?.rating ?? firstAnswer?.rating ?? null,
    //     explanation: existingAnswer?.explanation ?? firstAnswer?.text ?? '',
    //     selectedAnswerIds: existingAnswer?.selectedAnswerIds ?? [],
    //     duration: existingAnswer?.duration ?? 0,
    //     startTime: Date.now(),
    //   },
    // }));

    // console.log({
    //   [question.id]: {
    //     rating: existingAnswer?.rating ?? firstAnswer?.rating ?? null,
    //     explanation: existingAnswer?.explanation ?? firstAnswer?.text ?? '',
    //     selectedAnswerIds: existingAnswer?.selectedAnswerIds ?? [],
    //     duration: existingAnswer?.duration ?? 0,
    //     startTime: Date.now(),
    //   },
    // }, 'current question answers');


    window.scrollTo({ top: 0, behavior: 'smooth' });
    setError('');
  }, [question.id, setAnswers]);

  const handleRating = (value: number | null) => {
    setAnswers((prev) => ({
      ...prev,
      [question.id]: {
        ...prev[question.id],
        rating: value,
        edited: true,
      },
    }));
    setError('');
  };

  const handleExplanationChange = (value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [question.id]: {
        ...prev[question.id],
        explanation: value,
        edited: true,
      },
    }));
    setError('');
  };

  // Handle satisfaction scale selection (radio buttons)
  const handleSatisfactionScaleSelect = (answerId: string, text: string) => {
    setAnswers((prev) => ({
      ...prev,
      [question.id]: {
        ...prev[question.id],
        selectedAnswerIds: [answerId],
        explanation: text,
        edited: true,
      },
    }));
    setError('');
  };

  // Handle select type (checkboxes - multiple selection)
  const handleSelectToggle = (answerId: string, text: string) => {
    setAnswers((prev: any) => {
      const current = prev[question.id]?.selectedAnswerIds || [];
      const currentAnswers = prev[question.id]?.selectedAnswers || [];
      const isSelected = currentAnswers?.find((answer: any) => answer.answerId === answerId) !== undefined;
      //currentAnswers.some((answer: any) => answer.answerId === answerId);
      // console.log({ isSelected, currentAnswers });

      return {
        ...prev,
        [question.id]: {
          ...prev[question.id],
          selectedAnswers: isSelected
            ? currentAnswers.filter((answer: any) => answer.answerId !== answerId)
            : [...currentAnswers, { answerId, text }],
          edited: true,
          // selectedAnswerIds: isSelected
          //   ? current.filter(id => id !== answerId)
          //   : [...current, answerId],
        },
      };
    });
    setError('');
  };

  // const validateAnswer = () => {
  //   const current = answers[question.id];
  //   if (!current) return false;

  //   const type = question?.type?.toLowerCase() || '';
  //   const needsText = type.includes('free text');
  //   const needsRating = type.includes('rating') && !type.includes('satisfaction');
  //   const needsScoring = type.includes('scoring');
  //   const needsSatisfactionScale = type.includes('satisfaction scale');
  //   const needsSelect = type.includes('select');

  //   if (needsText && !current.explanation?.trim()) return false;
  //   if ((needsRating || needsScoring) && current.rating === null) return false;
  //   if ((needsSatisfactionScale || needsSelect) && (!current.selectedAnswerIds || current.selectedAnswerIds.length === 0)) return false;

  //   return true;
  // };

  const validateAnswer = () => {
    const current = answers[question.id];
    if (!current) return false;

    const type = question?.type?.toLowerCase() || '';
    const needsText = type.includes('free text') || type.includes('fill in the blank');
    const needsRating = type.includes('rating');
    const needsScoring = type.includes('scoring');
    const needsSatisfactionScale = type.includes('satisfaction scale');
    const needsSelect = type.includes('select');

    // "free text + rating" or "free text + scoring" must have both
    if ((needsText && needsRating) || (needsText && needsScoring)) {
      return current.explanation?.trim() && current.rating !== null;
    }

    if (needsText && !current.explanation?.trim()) return false;
    if ((needsRating || needsScoring) && current.rating === null) return false;
    if ((needsSatisfactionScale) && (!current.selectedAnswerIds?.length)) return false;
    if ((needsSelect) && (!current.selectedAnswers?.length)) return false;

    return true;
  };


  const saveArgs = (data: any) => ({
    reviewerId: assessmentType === 'review' ? userId : null,
    userId: assessmentType === 'review' ? activeAssessment?.user_id : userId,
    examId: activeAssessment?.exam_id,
    userExamId: window.sessionStorage.getItem('active-assessment-id'),
    assessmentId: activeAssessmentId,
    data,
  }) as any;

  // The server adds each save's duration to what it already has, so only send
  // the time spent since the last auto-save, or it would be counted twice.
  const sentDurations = useRef<{ [questionId: string]: number }>({});
  const unsentDuration = (questionId: string, total: number) =>
    Math.max(0, (total || 0) - (sentDurations.current[questionId] || 0));

  // Answers so far, leaving out unanswered questions (sending those would
  // clear them on the server). Used by Save Draft and auto-save.
  const buildDraftPayload = () => {
    const q = questions.map((q) => {
      const a = getAnswerState(q);

      const type = q?.type?.toLowerCase() || '';
      let answersArray = [];

      if (type.includes('satisfaction scale')) {
        answersArray = a.selectedAnswerIds.map(answerId => ({
          answer_id: answerId,
          answer_text: a.explanation,
          rating: null,
          status: true,
        }));
      }
      else if (type.includes('select')) {
        answersArray = a.selectedAnswers.map(answer => ({
          answer_id: answer.answerId,
          answer_text: answer.text,
          rating: null,
          status: true,
        }));
      }
      else {
        answersArray = [{
          answer_id: null,
          answer_text: a.explanation,
          rating: a.rating,
          status: true,
        }];
      }

      return {
        question_id: q.id,
        duration: unsentDuration(q.id, a.duration),
        answers: answersArray,
      };
    });

    return {
      time_left: 3560,
      attempt: 1,
      questions: q?.length ? q?.filter(question =>
        question?.answers?.some((ans: any) =>
          (ans?.rating || ans?.answer_text || ans?.answer_id)
        )
      ) : [],
    };
  };

  // Every AUTO_SAVE_EVERY questions, answers so far are saved in the
  // background, so a closed tab or lost connection costs at most a few
  // questions. Save Draft and Submit still save everything as before.
  const AUTO_SAVE_EVERY = 5;
  const autoSaveInFlight = useRef<Promise<void> | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = React.useState<'idle' | 'saving' | 'saved'>('idle');
  const autoSaveMutation = useMutation(
    (assessmentType === 'review' ? exams.updateAnswersv2Review : exams.updateAnswersv2) as any
  );

  const autoSave = () => {
    if (autoSaveInFlight.current) return;
    if (!hasValidSession()) {
      setSessionExpired(true);
      return;
    }
    const payload = buildDraftPayload();
    if (!payload.questions.length) return;

    setAutoSaveStatus('saving');
    autoSaveInFlight.current = autoSaveMutation
      .mutateAsync(saveArgs(payload))
      .then(() => {
        payload.questions.forEach((q: any) => {
          sentDurations.current[q.question_id] = (sentDurations.current[q.question_id] || 0) + q.duration;
        });
        setAutoSaveStatus('saved');
      })
      .catch((err: any) => {
        // Not shown to the participant: their answers are still on the device
        // and Save Draft / Submit will send them. Only an expired session,
        // which would also stop those, is worth interrupting for.
        console.error('Auto-save failed', err?.response?.status, err?.response?.data || err);
        setAutoSaveStatus('idle');
        if (isSessionError(err)) setSessionExpired(true);
      })
      .finally(() => {
        autoSaveInFlight.current = null;
      });
  };

  const saveDuration = () => {
    setAnswers((prev) => {
      const q = prev[question.id];
      const duration = q?.duration ?? 0;
      const startTime = q?.startTime ?? Date.now();
      return {
        ...prev,
        [question.id]: {
          ...q,
          duration: duration + Math.floor((Date.now() - startTime) / 1000),
        },
      };
    });
  };

  const handlePrev = () => {
    saveDuration();
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
    setError('');
  };

  const handleNext = () => {
    if (!validateAnswer()) {
      const type = question?.type?.toLowerCase() || '';
      if (type.includes('satisfaction scale') || type.includes('select')) {
        setError('Please select an option before continuing.');
      } else if ((type.includes('free text') || type.includes('fill in the blank')) && !currentAnswer.explanation) {
        setError(type.includes('fill in the blank') ? 'Please provide an answer before continuing.' : 'Please provide an explanation before continuing.');
      } else {
        setError('Please provide a valid rating before continuing.');
      }
      return;
    }
    saveDuration();
    setCurrentIndex((prev) => Math.min(prev + 1, questions.length - 1));

    const answeredSoFar = currentIndex + 1;
    if (answeredSoFar % AUTO_SAVE_EVERY === 0 && answeredSoFar < questions.length) {
      autoSave();
    }
  };

  const handleSubmit = async () => {
    if (!validateAnswer()) {
      setError('Please complete the required fields before submitting.');
      return;
    }
    if (!hasValidSession()) {
      setSessionExpired(true);
      return;
    }
    saveDuration();
    // Let a background save finish first so the two don't overlap.
    if (autoSaveInFlight.current) await autoSaveInFlight.current;

    const payload = {
      time_left: 3560,
      attempt: 1,
      questions: questions.map((q) => {
        const a = getAnswerState(q);

        // For satisfaction scale and select types, create answer objects for each selected ID
        const type = q?.type?.toLowerCase() || '';
        let answersArray = [];

        if (type.includes('satisfaction scale')) {
          answersArray = a.selectedAnswerIds.map(answerId => ({
            answer_id: answerId,
            answer_text: a.explanation,
            rating: null,
            status: true,
          }));
        }
        else if (type.includes('select')) {
          answersArray = a.selectedAnswers.map(answer => ({
            answer_id: answer.answerId,
            answer_text: answer.text,
            rating: null,
            status: true,
          }));
        }
        else {
          answersArray = [{
            answer_id: null,
            answer_text: a.explanation || null,
            rating: a.rating || null,
            status: true,
          }];
        }

        return {
          question_id: q.id,
          duration: unsentDuration(q.id, a.duration),
          answers: answersArray,
        };
      }),
    };
    //return console.log(payload, 'payload');

    updateAnswersMutation.mutate(saveArgs(payload));
  };

  const handleSaveDraft = async () => {
    if (!hasValidSession()) {
      setSessionExpired(true);
      return;
    }
    if (autoSaveInFlight.current) await autoSaveInFlight.current;
    draftAnswersMutation.mutate(saveArgs(buildDraftPayload()));
  };

  const config = useMemo(() => ({
    readonly: false,
    placeholder: '',
    style: {
      ul: { 'list-style-type': 'disc', 'padding-left': '20px' },
      ol: { 'list-style-type': 'decimal', 'padding-left': '20px' }
    }
  }), []);

  // Extract rating value from satisfaction scale text (e.g., "Very Satisfied=5" -> 5)
  const extractRatingFromText = (text: string): number | null => {
    const match = text.match(/=(\d+)$/);
    return match ? parseInt(match[1]) : null;
  };

  // Get clean label for satisfaction scale
  const getCleanLabel = (text: string): string => {
    return text.split('=')[0];
  };

  const questionType = question?.type?.toLowerCase() || '';

  //console.log({ answers });

  return (
    <div className='text-lg'>
      {sessionExpired && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="session-expired-title"
          className="fixed inset-0 z-50 grid place-items-center bg-black bg-opacity-50 p-4"
        >
          <div className="w-full max-w-[480px] bg-white rounded-2xl p-6 md:p-8 text-center">
            <h3 id="session-expired-title" className="text-xl font-semibold text-gray-900">
              Your session has expired
            </h3>
            <p className="mt-3 text-base text-gray-600">
              Your answers are saved on this device. Sign in again to continue where you left off.
            </p>
            <button
              type="button"
              onClick={signInAgain}
              className="w-full mt-6 rounded-md bg-primary text-white text-base py-2"
              style={{ backgroundColor: themeColour }}
            >
              Continue
            </button>
          </div>
        </div>
      )}
      <header className={`bg-white px-3 rounded-lg py-2 mt-2 ${isCultureAudit ? 'border border-gray-200' : ''}`}>
        <div className='flex justify-between mb-2'>
          <h1 className={`text-lg ${large('lg:text-[20px]')}`}>
            Question {currentIndex + 1} of {questions.length}
            {autoSaveStatus !== 'idle' && (
              <span className='ml-3 text-sm text-gray-500' aria-live='polite'>
                {autoSaveStatus === 'saving' ? 'Saving…' : 'Progress saved'}
              </span>
            )}
          </h1>
          {!isCultureAudit && (
            <p className='text-lg'>
              <b className='text-lg'>Section</b>: {question?.section?.title}
            </p>
          )}
        </div>
        <ProgressBar current={currentIndex + 1} total={questions.length} colour={themeColour} />
      </header>

      <div className={`bg-white mt-8 p-5 pt-2 rounded-lg mx-auto md:max-w-[700px] ${isCultureAudit ? 'border border-gray-200 lg:max-w-[85%] lg:mt-[40px] lg:px-[48px] lg:pt-[32px] lg:pb-[40px]' : ''}`}>
        <p className='text-lg text-primary' style={{ color: themeColour }}>
          <b className={`text-lg ${large('lg:text-[24px]')}`}>{question?.tag}</b>
        </p>

        <section className='grid grid-cols-1 gap-0'>
          <div className={`md:max-w-[700px] ${large('lg:max-w-none')}`}>
            {activeAssessment?.can_access_participant_responses ?
              <div className='flex justify-end'>
                <button 
                  onClick={() => setIsResponseModalOpen(true)}
                  className='bg-darkBlue text-white px-4 py-2 rounded-lg'>
                  View Participant Response
                </button>
              </div> : null}
            <h2 className={`text-lg mt-5 mb-3 ${large('lg:text-[30px] lg:leading-snug lg:mt-[24px] lg:mb-[32px]')}`}>{question.text}</h2>

            {/* Free Text Editor */}
            {(questionType.includes('free text') || questionType.includes('free_text')) && (
              <div className='py-3 editor-container'>
                <JoditEditor
                  ref={editor}
                  value={currentAnswer.explanation ?? ''}
                  config={config}
                  tabIndex={1}
                  onBlur={newContent => {
                    handleExplanationChange(newContent);
                  }}
                />
              </div>
            )}

            {/* Fill in the Blank - Short Text Input */}
            {questionType.includes('fill in the blank') && (
              <div className='py-3'>
                <input
                  type="text"
                  value={currentAnswer.explanation ?? ''}
                  onChange={(e) => handleExplanationChange(e.target.value)}
                  className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-primary'
                  placeholder="Type your answer"
                />
              </div>
            )}

            {/* Scoring Type - Number Input */}
            {questionType.includes('scoring') && (
              <section>
                <div className='flex flex-col mt-3'>
                  <p className='text-lg font-semibold mb-2'>
                    Score <span className='font-normal'>(Required)</span>
                  </p>
                  <div className='flex items-center'>
                    <input
                      type="number"
                      min={question.rating_range_from || 0}
                      max={question.rating_range_to || 10}
                      step="0.1"
                      value={currentAnswer.rating ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        const numValue = value === '' ? null : parseFloat(value);

                        if (numValue !== null) {
                          if (numValue < (question.rating_range_from || 0) ||
                            numValue > (question.rating_range_to || 10)) {
                            setError(`Score must be between ${question.rating_range_from || 0} and ${question.rating_range_to || 10}`);
                          } else {
                            setError('');
                          }
                        }

                        handleRating(numValue);
                      }}
                      className='w-24 h-10 border border-gray-300 rounded-md px-3 focus:outline-none focus:border-primary'
                      placeholder="Score"
                    />
                    {/* <span className='ml-2 text-gray-600'>
                      (Range: {question.rating_range_from || 0} - {question.rating_range_to || 10})
                    </span> */}
                  </div>
                  {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                </div>
              </section>
            )}

            {/* Regular Rating Type - Star Rating */}
            {questionType.includes('rating') && !questionType.includes('satisfaction') && (
              <section>
                <div className='flex items-center justify-between mt-3'>
                  <p className='text-lg font-semibold'>
                    Rating <span className='font-normal'>(Required)</span>
                  </p>
                  <div className='flex items-center'>
                    <Rating
                      name={`rating-${question.id}`}
                      value={currentAnswer.rating ?? 0}
                      precision={1}
                      sx={{ "& .MuiRating-icon": { fontSize: "40px" } }}
                      onChange={(event, newValue) => {
                        handleRating(newValue);
                      }}
                      max={question.rating_range_to || 5}
                      onChangeActive={(event, newHover) => setHover(newHover)}
                      emptyIcon={<StarIcon style={{ opacity: 0.3, fontSize: "40px" }} />}
                    />
                    {currentAnswer.rating !== null && (
                      <h2 className='text-xl pl-2'>{currentAnswer.rating}</h2>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* Satisfaction Scale - Radio Buttons Grid */}
            {questionType.includes('satisfaction scale') && (
              <div className='mt-5'>
                <div className='overflow-x-auto'>
                  <table className='w-full border-collapse'>
                    <thead>
                      <tr className='border-b-2 border-gray-300'>
                        {/* <th className='text-left p-3 font-normal'></th> */}
                        {question.answers
                          .sort((a, b) => {
                            const ratingA = extractRatingFromText(a.text) || 0;
                            const ratingB = extractRatingFromText(b.text) || 0;
                            return ratingA - ratingB;
                          })
                          .map((answer) => (
                            <th key={answer.id} className={`text-center p-3 font-semibold min-w-[100px] ${large('lg:text-[20px] lg:p-[16px]')}`}>
                              {getCleanLabel(answer.text)}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className='border-b border-gray-200'>
                        {/* <td className='p-3 pr-8'>{question.text}</td> */}
                        {question.answers
                          .sort((a, b) => {
                            const ratingA = extractRatingFromText(a.text) || 0;
                            const ratingB = extractRatingFromText(b.text) || 0;
                            return ratingA - ratingB;
                          })
                          .map((answer) => (
                            <td key={answer.id} className={`text-center p-3 ${large('lg:p-[20px]')}`}>
                              <input
                                type='radio'
                                name={`satisfaction-${question.id}`}
                                checked={currentAnswer?.selectedAnswerIds?.includes(answer?.id)}
                                onChange={() => handleSatisfactionScaleSelect(answer.id, getCleanLabel(answer.text))}
                                className={`w-6 h-6 cursor-pointer accent-primary ${large('lg:w-[32px] lg:h-[32px]')}`}
                                style={{ accentColor: themeColour }}
                              />
                            </td>
                          ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Select Type - Checkboxes */}
            {questionType.includes('select') && (
              <div className='mt-5 space-y-3'>
                {question.answers.map((answer) => (
                  <label
                    key={answer.id}
                    className='flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition'
                  >
                    <input
                      type='checkbox'
                      checked={currentAnswer?.selectedAnswers?.find((a: any) => a?.answerId === answer?.id)
                        === undefined ? false : true
                      }
                      onChange={() => handleSelectToggle(answer.id, answer.text)}
                      className='w-5 h-5 cursor-pointer accent-primary mr-3'
                      style={{ accentColor: themeColour }}
                    />
                    <span className={`text-base ${large('lg:text-[20px]')}`}>{answer.text}</span>
                  </label>
                ))}
              </div>
            )}

            {error && <p className='text-red-500 mt-3 text-sm'>{error}</p>}
          </div>
        </section>

        <section className={`flex flex-col sm:flex-row justify-between bg-white mx-auto md:max-w-[700px] gap-3 ${large('lg:max-w-none lg:mt-[16px]')}`}>
          <button
            disabled={currentIndex === 0}
            className={`border border-primary text-primary font-semibold rounded-lg py-3 px-6 sm:px-12 md:px-16 mt-5 ${large('lg:text-[20px] lg:py-[16px] lg:px-[64px]')} ${currentIndex === 0
              ? 'opacity-50 cursor-not-allowed'
              : ''
              }`}
            style={{ borderColor: themeColour, color: themeColour }}
            onClick={handlePrev}
          >
            Previous
          </button>

          <div className='flex flex-col sm:flex-row items-center gap-3 sm:gap-5'>
            <button
              // Branded culture audits: Next already uses the brand colour, so
              // Save Draft goes black to keep the two buttons distinct.
              className={`${isCultureAudit && themeColour ? 'bg-gray-900' : 'bg-orange-500'} rounded-lg text-white py-3 px-6 sm:px-12 md:px-16 mt-5 w-full sm:w-auto ${large('lg:text-[20px] lg:py-[16px] lg:px-[64px]')}`}
              onClick={() => handleSaveDraft()}
            >
              Save Draft
            </button>
            {currentIndex === questions.length - 1 ? (
              <button
                className={`bg-primary rounded-lg text-white py-3 px-6 sm:px-12 md:px-16 mt-5 w-full sm:w-auto ${large('lg:text-[20px] lg:py-[16px] lg:px-[64px]')}`}
                style={{ backgroundColor: themeColour }}
                onClick={handleSubmit}
                disabled={updateAnswersMutation.isLoading || finishSurveyMutation.isLoading}
              >
                {
                  (updateAnswersMutation.isLoading || finishSurveyMutation.isLoading) ? <LoaderIcon /> : 'Submit'
                }
              </button>
            ) : (
              <button
                className={`bg-primary rounded-lg text-white py-3 px-6 sm:px-12 md:px-16 mt-5 w-full sm:w-auto ${large('lg:text-[20px] lg:py-[16px] lg:px-[64px]')}`}
                style={{ backgroundColor: themeColour }}
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>

        </section>
      </div>

      {isResponseModalOpen && (
        <Modal title="Participant Response" closeModal={() => setIsResponseModalOpen(false)}>
          {participantResponsesLoading ? (
            <p>Loading response...</p>
          ) : currentParticipantResponse ? (
            <div className="flex flex-col gap-4">
              <h3 className="font-semibold text-lg">{currentParticipantResponse.question?.text}</h3>
              {currentParticipantResponse.questionSpoolAnswers?.map((ans: any, idx: number) => (
                <div key={ans.id || idx} className="bg-gray-50 p-4 rounded-lg">
                  {ans.rating && (
                    <p className="mb-2"><strong>Rating:</strong> {ans.rating}</p>
                  )}
                  {ans.answer_text && (
                    <div>
                      <strong>Answer:</strong>
                      <div dangerouslySetInnerHTML={{ __html: ans.answer_text }} className="mt-1 prose text-gray-700 max-w-none" />
                    </div>
                  )}
                </div>
              ))}
              {(!currentParticipantResponse.questionSpoolAnswers || currentParticipantResponse.questionSpoolAnswers.length === 0) && (
                <p className="text-gray-500">No answer provided.</p>
              )}
            </div>
          ) : (
            <p className="text-gray-500">No response found for this question.</p>
          )}
        </Modal>
      )}
    </div>
  );
};

export default Employee